import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { PERMISSION_OBJECTS, isLockedCell } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { SearchInput } from '../../../../components/Input';
import { Select } from '../../../../components/Select';
import { SkeletonLines } from '../../../../components/Skeleton';
import type { RoleSummary } from '../../api/roles-api';
import styles from './RoleList.module.css';

// Roles A: the roles on the left, each with how many people hold it, and "Find a role". At laptop
// width and below the list is a drop-down above the editor instead (the mockup's note 5).

export function peopleText(count: number): string {
  return count === 1 ? '1 person' : `${count} people`;
}

/** The line under a role's name: standard, or where a copy came from. */
export function roleOrigin(role: Pick<RoleSummary, 'isSystem' | 'key' | 'copiedFromLabel'>): string {
  if (role.isSystem) return PERMISSION_OBJECTS.some((object) => isLockedCell(role.key, object)) ? 'Standard · some boxes fixed' : 'Standard';
  return role.copiedFromLabel ? `Copy of ${role.copiedFromLabel}` : 'Started blank';
}

/**
 * The roles offered on this page. `candidate` holds no internal permission anywhere, so it is never
 * offered. The old Approver preset is kept for old data but no longer given: it shows only while
 * somebody still holds it.
 */
export function listedRoles<T extends RoleSummary>(roles: readonly T[]): readonly T[] {
  return roles.filter((role) => role.key !== 'candidate' && !(role.key === 'approver' && role.assignedUserCount === 0));
}

interface RoleListProps {
  readonly roles: readonly RoleSummary[];
  readonly isLoading: boolean;
  readonly selectedId: string | null;
  readonly onSelect: (id: string) => void;
}

export function RoleList({ roles, isLoading, selectedId, onSelect }: RoleListProps): ReactNode {
  const [query, setQuery] = useState('');
  const listRef = useRef<HTMLUListElement | null>(null);
  const needle = query.trim().toLowerCase();
  const shown = needle === '' ? roles : roles.filter((role) => role.label.toLowerCase().includes(needle));

  // 06-qol §2: every list moves with the arrow keys.
  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>): void => {
    const step = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
    if (step === 0 || shown.length === 0) return;
    event.preventDefault();
    const index = shown.findIndex((role) => role.id === selectedId);
    const next = shown[Math.min(shown.length - 1, Math.max(0, index + step))];
    if (!next) return;
    onSelect(next.id);
    listRef.current?.querySelectorAll<HTMLButtonElement>('button')[shown.indexOf(next)]?.focus();
  };

  return (
    <nav className={styles.pane} aria-label="Roles">
      <SearchInput aria-label="Find a role" placeholder="Find a role" value={query} onChange={(event) => setQuery(event.target.value)} />
      {isLoading ? (
        <SkeletonLines count={6} />
      ) : shown.length === 0 ? (
        <div className={styles.empty}>
          <span>No role matches “{query.trim()}”.</span>
          <Button variant="ghost" size="sm" onClick={() => setQuery('')}>
            Clear
          </Button>
        </div>
      ) : (
        <ul ref={listRef} className={styles.list} onKeyDown={onKeyDown}>
          {shown.map((role) => (
            <li key={role.id}>
              <button
                type="button"
                className={styles.item}
                aria-current={role.id === selectedId}
                tabIndex={role.id === selectedId || (selectedId === null && role === shown[0]) ? 0 : -1}
                onClick={() => onSelect(role.id)}
              >
                <span className={styles.text}>
                  <span className={styles.name}>{role.label}</span>
                  <span className={styles.origin}>{roleOrigin(role)}</span>
                </span>
                <span className={styles.count} aria-label={peopleText(role.assignedUserCount)}>
                  {role.assignedUserCount}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </nav>
  );
}

/** Laptop, tablet and phone: the role is picked from a drop-down ("Interviewer · 84 people"). */
export function RolePicker({ roles, selectedId, onSelect }: Omit<RoleListProps, 'isLoading'>): ReactNode {
  return (
    <div className={styles.picker}>
      <Select
        label="Role"
        value={selectedId}
        onChange={onSelect}
        options={roles.map((role) => ({
          value: role.id,
          label: `${role.label} · ${peopleText(role.assignedUserCount)}`,
          description: roleOrigin(role),
          searchText: role.label,
        }))}
      />
    </div>
  );
}
